import type { CSSProperties } from "react";
import { useEntranceMotion } from "@/hooks/useEntranceMotion";

// Attention caught by an extreme and pulled back into the target. `from` sits
// near an extreme and `to` inside the ring, both in SVG user units from the
// point where the axes cross; delays stagger the loop.
const pulls = [
  { from: [-124, -10], to: [-22, -5], delay: 0 },
  { from: [122, 12], to: [22, 6], delay: 0.6 },
  { from: [12, -122], to: [7, -22], delay: 1.2 },
  { from: [-10, 124], to: [-6, 23], delay: 1.8 },
  { from: [-116, 16], to: [-15, 17], delay: 2.4 },
  { from: [114, -14], to: [16, -16], delay: 3 },
  { from: [-16, -114], to: [-16, -16], delay: 3.6 },
  { from: [14, 116], to: [17, 16], delay: 4.2 },
];

/** Animated rendering of the "state of AI" sketch: the conversation pulls toward
 * four extremes, and the useful work is drawn back to the center, under the
 * 10-80-10 rule. The finished frame is the server-rendered default, with every
 * dot settled in the ring, so the graphic is complete without JavaScript and
 * for people who prefer reduced motion.
 */
const StateOfAIExplainer = () => {
  const [card, motion] = useEntranceMotion<HTMLElement>();

  return (
    <figure ref={card} className="home-sketch-card home-state-card" data-motion={motion}>
      <div
        className="home-sketch-plot"
        role="img"
        aria-label="The state of AI, drawn as two crossing axes: hype at the top, let down at the bottom, doom and gloom on the left, and utopia on the right. Most of the conversation drifts toward those edges. At the center, where the axes cross, is the 10-80-10 rule, and it draws the work back into the middle."
      >
        <svg viewBox="0 0 520 400" aria-hidden="true">
          <path className="home-sketch-axis" d="M260 56V344" pathLength={1} />
          <path className="home-sketch-axis" d="M120 200H400" pathLength={1} />
          <g className="home-sketch-dots">
            {pulls.map(({ from: [fromX, fromY], to: [toX, toY], delay }) => (
              <circle
                key={`${fromX} ${fromY}`}
                cx="260"
                cy="200"
                r="5"
                style={
                  {
                    "--from-x": `${fromX}px`,
                    "--from-y": `${fromY}px`,
                    "--to-x": `${toX}px`,
                    "--to-y": `${toY}px`,
                    "--delay": `${delay}s`,
                  } as CSSProperties
                }
              />
            ))}
          </g>
          <circle className="home-state-halo" cx="260" cy="200" r="40" />
          <path
            className="home-state-ring"
            d="M300 200A40 40 0 1 1 220 200A40 40 0 1 1 300 200"
            pathLength={1}
          />
          <circle className="home-state-dot" cx="260" cy="200" r="6" />
        </svg>
        <span className="home-sketch-label" data-pole="hype">
          <span className="home-sketch-mark">Hype</span>
        </span>
        <span className="home-sketch-label" data-pole="utopia">
          <span className="home-sketch-mark">Utopia</span>
        </span>
        <span className="home-sketch-label" data-pole="letdown">
          <span className="home-sketch-mark">Let down</span>
        </span>
        <span className="home-sketch-label" data-pole="doom">
          <span className="home-sketch-mark">Doom &amp;</span>
          <span className="home-sketch-mark">gloom</span>
        </span>
        <span className="home-sketch-label" data-pole="center" data-tone="gain">
          <span className="home-sketch-mark">The 10-80-10 rule</span>
        </span>
      </div>
      {/* The 10-80-10 list beside this graphic carries the same content as text. */}
      <div className="home-state-split" aria-hidden="true">
        <div className="home-state-bar">
          <span data-part="people">10</span>
          <span data-part="ai">80</span>
          <span data-part="people">10</span>
        </div>
        <div className="home-state-bar-labels">
          <span>People</span>
          <span>AI</span>
          <span>People</span>
        </div>
      </div>
    </figure>
  );
};

export default StateOfAIExplainer;
